import {
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
  reload,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type MultiFactorResolver,
  type TotpSecret,
  type User,
} from 'firebase/auth'
import { navigateTo } from 'nuxt/app'
import {
  ApiResponseError,
  getBackendClient,
  unwrapApiResponse,
  type ApiSchema,
  type BackendClient,
} from '~/api/client'
import { NuxtRoutes } from '~/constants/routes'
import { normalizeEmailInput } from '~/utils/normalizeInputs'

export interface MfaStatus {
  mfaAllowed: boolean
  totpMfaRequired: boolean
  emailMfaEnabled: boolean
  firebaseTotpEnrolled: boolean
  localTotpEnrolled: boolean
  enrollmentRequired: boolean
  enrollmentId?: string | null
  rememberDeviceDays: number
  verificationRequired: boolean
  availableMethods: Array<'totp' | 'email'>
}

export type MfaSyncResult
  = | { ok: true, status: MfaStatus | null }
    | { ok: false, error: unknown }

export interface PendingEmailMfaChallenge {
  challengeId: string
  browserVerifier: string
  maskedEmail: string
  preparedExpiresAt: string
  rememberDeviceDays: number
  codeExpiresAt: string | null
  resendAvailableAt: string | null
}
export interface TrustedDeviceRestoreResult {
  customToken: string | null
  rememberDeviceDays: number
  restored: boolean
}

export interface TrustedDevice {
  id: string
  userAgentLabel: string | null
  lastIpAddress: string | null
  createdAt: string
  lastUsedAt: string | null
  expiresAt: string
  currentDevice: boolean
}

export interface TotpReauthenticationChallenge {
  resolver: MultiFactorResolver
  factorUid: string
}

export type MfaErrorContext = 'challenge' | 'setup' | 'setupInit'

export const MFA_SESSION_LOST_KEY = 'mfaSessionLost'
export const MFA_SETUP_WAS_IN_PROGRESS_KEY = 'mfaSetupWasInProgress'
export const EMAIL_MFA_CHALLENGE_KEY = 'emailMfaChallenge'

const pendingMfaResolver = shallowRef<MultiFactorResolver | null>(null)
const pendingMfaRememberDeviceDays = ref(0)
const pendingEmailMfaChallenge = shallowRef<PendingEmailMfaChallenge | null>(null)

/** How long a cleared MFA status may be reused by the router guard. */
export const MFA_STATUS_CACHE_TTL_MS = 5 * 60 * 1000

type MfaStatusRequestOptions = { force?: boolean }

let cachedMfaStatus: MfaStatus | null | undefined
let cachedMfaStatusAt = 0
let cachedMfaStatusUserId: string | null = null
let inFlightMfaStatus: Promise<MfaStatus | null> | null = null

const isCacheableMfaStatus = (status: MfaStatus | null): status is MfaStatus =>
  status !== null
  && !status.enrollmentRequired
  && !status.verificationRequired

export const clearMfaStatusCache = () => {
  cachedMfaStatus = undefined
  cachedMfaStatusAt = 0
  cachedMfaStatusUserId = null
  inFlightMfaStatus = null
}

const readCachedMfaStatus = (): MfaStatus | undefined => {
  if (cachedMfaStatus === undefined || !isCacheableMfaStatus(cachedMfaStatus)) {
    return undefined
  }
  const userId = getAuth().currentUser?.uid ?? null
  if (userId !== cachedMfaStatusUserId) {
    clearMfaStatusCache()
    return undefined
  }
  if (Date.now() - cachedMfaStatusAt > MFA_STATUS_CACHE_TTL_MS) {
    clearMfaStatusCache()
    return undefined
  }
  return cachedMfaStatus
}

const writeCachedMfaStatus = (status: MfaStatus | null) => {
  if (!isCacheableMfaStatus(status)) {
    clearMfaStatusCache()
    return
  }
  cachedMfaStatus = status
  cachedMfaStatusAt = Date.now()
  cachedMfaStatusUserId = getAuth().currentUser?.uid ?? null
}

const fromApiMfaStatus = (status: ApiSchema<'GetMfaStatus'>): MfaStatus => ({
  mfaAllowed: status.mfa_allowed ?? true,
  totpMfaRequired: status.totp_mfa_required,
  emailMfaEnabled: Boolean(status.email_mfa_enabled),
  firebaseTotpEnrolled: status.firebase_totp_enrolled,
  localTotpEnrolled: status.local_totp_enrolled,
  enrollmentRequired: status.enrollment_required,
  enrollmentId: status.enrollment_id ?? null,
  rememberDeviceDays: Number(status.remember_device_days ?? 0),
  verificationRequired: Boolean(status.verification_required),
  availableMethods: (status.available_methods ?? []) as Array<'totp' | 'email'>,
})

const fromApiTrustedDeviceRestore = (
  result: ApiSchema<'GetTrustedDeviceRestore'>,
): TrustedDeviceRestoreResult => ({
  customToken: result.custom_token ?? null,
  rememberDeviceDays: Number(result.remember_device_days ?? 0),
  restored: Boolean(result.restored),
})

const fromApiTrustedDevice = (device: ApiSchema<'GetTrustedDevice'>): TrustedDevice => ({
  id: device.id,
  userAgentLabel: device.user_agent_label ?? null,
  lastIpAddress: device.last_ip_address ?? null,
  createdAt: new Date(device.created_at).toISOString(),
  lastUsedAt: device.last_used_at ? new Date(device.last_used_at).toISOString() : null,
  expiresAt: new Date(device.expires_at).toISOString(),
  currentDevice: Boolean(device.current_device),
})

const createMfaApi = (options: { auth?: boolean, withCredentials?: boolean } = {}): BackendClient =>
  getBackendClient({
    auth: options.auth,
    credentials: options.withCredentials ? 'include' : undefined,
  })

const createAuthenticatedMfaApi = async () => {
  const token = await getCurrentToken()
  if (!token) throw new Error('No authenticated user')
  return createMfaApi({ withCredentials: true })
}

const rethrowMfaApiError = (error: unknown, message: string): never => {
  if (error instanceof ApiResponseError) {
    throw new Error(`${message} with ${error.response.status}`)
  }
  throw error
}

export const getEmailMfaErrorCode = (error: unknown): string | null => {
  if (!(error instanceof ApiResponseError)) return null
  const detail = (error.error as { detail?: unknown } | null)?.detail
  if (!detail || typeof detail !== 'object') return null
  const code = (detail as { error_code?: unknown }).error_code
  return typeof code === 'string' ? code : null
}
const isExpectedMfaStatusAuthError = (error: unknown): boolean =>
  error instanceof Error && error.message === 'MFA status request failed with 401'

const getCurrentToken = async () => {
  const user = getAuth().currentUser
  return user ? await user.getIdToken() : null
}

export const normalizeTotpCode = (input: string): string =>
  input.replace(/\D/g, '').slice(0, 6)

export const isValidTotpCode = (input: string): boolean =>
  /^\d{6}$/.test(normalizeTotpCode(input))

export const getFirebaseErrorCode = (error: unknown): string | undefined => {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code
    if (typeof code === 'string') {
      // Custom-token sessions (email MFA) cannot start MFA enrollment until the
      // user reauthenticates with password; Firebase returns this as a code or
      // as an Identity Toolkit message containing UNSUPPORTED_FIRST_FACTOR.
      if (
        code === 'auth/unsupported-first-factor'
        || code.includes('UNSUPPORTED_FIRST_FACTOR')
      ) {
        return 'auth/unsupported-first-factor'
      }
      return code
    }
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string') {
      if (message.includes('SECOND_FACTOR_LIMIT_EXCEEDED')) {
        return 'auth/maximum-second-factor-count-exceeded'
      }
      if (message.includes('UNSUPPORTED_FIRST_FACTOR')) {
        return 'auth/unsupported-first-factor'
      }
    }
  }

  return undefined
}

export const isSecondFactorLimitExceededError = (error: unknown): boolean =>
  getFirebaseErrorCode(error) === 'auth/maximum-second-factor-count-exceeded'

export const getMfaErrorMessageKey = (
  error: unknown,
  context: MfaErrorContext,
): string => {
  const code = getFirebaseErrorCode(error)

  switch (code) {
    case 'auth/invalid-verification-code':
      return 'auth.mfa.invalidVerificationCode'
    case 'auth/too-many-requests':
      return 'auth.mfa.tooManyRequests'
    case 'auth/maximum-second-factor-count-exceeded':
      return 'auth.mfa.secondFactorLimitExceeded'
    case 'auth/requires-recent-login':
    case 'auth/unsupported-first-factor':
      return 'auth.mfa.requiresRecentLogin'
    case 'auth/missing-multi-factor-info':
    case 'auth/invalid-multi-factor-session':
    case 'auth/totp-challenge-timeout':
      return 'auth.mfa.sessionExpiredChallenge'
    default:
      if (context === 'setupInit') return 'auth.mfa.setupInitFailed'
      if (context === 'setup') return 'auth.mfa.setupFailed'
      return 'auth.mfa.challengeFailed'
  }
}

/** True when password reauth is required before MFA enroll/change (stale session or custom-token first factor). */
export const isRequiresRecentLoginError = (error: unknown): boolean => {
  const code = getFirebaseErrorCode(error)
  return code === 'auth/requires-recent-login'
    || code === 'auth/unsupported-first-factor'
}

export const isMfaChallengeSessionExpiredError = (error: unknown): boolean => {
  const code = getFirebaseErrorCode(error)
  return code === 'auth/missing-multi-factor-info'
    || code === 'auth/invalid-multi-factor-session'
    || code === 'auth/totp-challenge-timeout'
}

export const isMultiFactorAuthRequiredError = (error: unknown): boolean =>
  getFirebaseErrorCode(error) === 'auth/multi-factor-auth-required'

export const setMfaSessionLostReason = (reason: 'challenge') => {
  if (import.meta.client) {
    sessionStorage.setItem(MFA_SESSION_LOST_KEY, reason)
  }
}

export const consumeMfaSessionLostReason = (): string | null => {
  if (!import.meta.client) return null
  const reason = sessionStorage.getItem(MFA_SESSION_LOST_KEY)
  if (reason) {
    sessionStorage.removeItem(MFA_SESSION_LOST_KEY)
  }
  return reason
}

export const markMfaSetupInProgress = () => {
  if (import.meta.client) {
    sessionStorage.setItem(MFA_SETUP_WAS_IN_PROGRESS_KEY, '1')
  }
}

export const consumeMfaSetupWasInProgress = (): boolean => {
  if (!import.meta.client) return false
  const wasInProgress = sessionStorage.getItem(MFA_SETUP_WAS_IN_PROGRESS_KEY) === '1'
  if (wasInProgress) {
    sessionStorage.removeItem(MFA_SETUP_WAS_IN_PROGRESS_KEY)
  }
  return wasInProgress
}

export const clearMfaSetupInProgress = () => {
  if (import.meta.client) {
    sessionStorage.removeItem(MFA_SETUP_WAS_IN_PROGRESS_KEY)
  }
}

export const hasPendingMfaResolver = () => Boolean(pendingMfaResolver.value)

export const setPendingMfaResolverFromError = (error: unknown, rememberDeviceDays = 0) => {
  pendingMfaResolver.value = getMultiFactorResolver(
    getAuth(),
    error as MultiFactorError,
  )
  pendingMfaRememberDeviceDays.value = rememberDeviceDays
}

export const clearPendingMfaResolver = () => {
  pendingMfaResolver.value = null
  pendingMfaRememberDeviceDays.value = 0
}

export const getPendingMfaRememberDeviceDays = () => pendingMfaRememberDeviceDays.value

export const isFirebaseTotpEnrolled = (user: User) =>
  multiFactor(user).enrolledFactors.some(
    factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  )

export const getEnrolledTotpFactor = (user: User) =>
  multiFactor(user).enrolledFactors.find(
    factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  ) ?? null

export const reloadFirebaseUser = async (user: User): Promise<User> => {
  await reload(user)
  return getAuth().currentUser ?? user
}

export const isMfaEnrolled = (status: MfaStatus | null): boolean =>
  Boolean(status?.firebaseTotpEnrolled || status?.localTotpEnrolled)

export const canRemoveMfa = (status: MfaStatus | null): boolean =>
  isMfaEnrolled(status)
  && (!status?.totpMfaRequired || Boolean(status.emailMfaEnabled))

export const canSetupMfa = (status: MfaStatus | null): boolean =>
  Boolean(status?.mfaAllowed !== false)

export const unenrollTotpMfa = async (user: User): Promise<MfaStatus | null> => {
  clearMfaStatusCache()
  const refreshedUser = await reloadFirebaseUser(user)
  const factor = getEnrolledTotpFactor(refreshedUser)
  if (!factor) {
    return getMfaStatus({ force: true })
  }

  await multiFactor(refreshedUser).unenroll(factor.uid)
  const reloadedUser = await reloadFirebaseUser(refreshedUser)
  await reloadedUser.getIdToken(true)
  return getMfaStatus({ force: true })
}

export const getMfaStatus = async (
  options: MfaStatusRequestOptions = {},
): Promise<MfaStatus | null> => {
  if (!options.force) {
    const cached = readCachedMfaStatus()
    if (cached !== undefined) return cached
    if (inFlightMfaStatus) return inFlightMfaStatus
  }

  const token = await getCurrentToken()
  if (!token) {
    clearMfaStatusCache()
    return null
  }

  const fetchPromise = (async (): Promise<MfaStatus | null> => {
    try {
      const api = createMfaApi()
      const status = fromApiMfaStatus(await unwrapApiResponse(api.GET('/mfa/status')))
      writeCachedMfaStatus(status)
      return status
    }
    catch (error) {
      return rethrowMfaApiError(error, 'MFA status request failed')
    }
  })()

  if (!options.force) {
    inFlightMfaStatus = fetchPromise
  }

  try {
    return await fetchPromise
  }
  finally {
    if (inFlightMfaStatus === fetchPromise) {
      inFlightMfaStatus = null
    }
  }
}

/** Sync backend enrollment when Firebase already has TOTP but local DB does not. */
export const syncMfaEnrollmentIfNeeded = async (
  options: MfaStatusRequestOptions = {},
): Promise<MfaStatus | null> => {
  const status = await getMfaStatus(options)
  if (!status?.enrollmentRequired) return status

  const user = getAuth().currentUser
  const firebaseEnrolled
    = status.firebaseTotpEnrolled || (user ? isFirebaseTotpEnrolled(user) : false)

  if (!firebaseEnrolled) return status

  try {
    return await markTotpEnrolled()
  }
  catch (error) {
    $reportError('Failed to sync MFA enrollment with backend:', error)
    return status
  }
}

/** Like syncMfaEnrollmentIfNeeded but never throws — for route guards. */
export const syncMfaEnrollmentIfNeededSafe = async (
  options: MfaStatusRequestOptions = {},
): Promise<MfaSyncResult> => {
  try {
    const status = await syncMfaEnrollmentIfNeeded(options)
    return { ok: true, status }
  }
  catch (error) {
    if (!isExpectedMfaStatusAuthError(error)) {
      $reportError('Failed to sync MFA enrollment:', error)
    }
    return { ok: false, error }
  }
}

export const markTotpEnrolled = async (): Promise<MfaStatus> => {
  try {
    const api = await createAuthenticatedMfaApi()
    const status = fromApiMfaStatus(await unwrapApiResponse(api.POST('/mfa/totp/mark-enrolled')))
    writeCachedMfaStatus(status)
    return status
  }
  catch (error) {
    return rethrowMfaApiError(error, 'MFA enrollment sync failed')
  }
}

const persistEmailMfaChallenge = (challenge: PendingEmailMfaChallenge | null) => {
  pendingEmailMfaChallenge.value = challenge
  if (!import.meta.client) return
  if (challenge) {
    sessionStorage.setItem(EMAIL_MFA_CHALLENGE_KEY, JSON.stringify(challenge))
  }
  else {
    sessionStorage.removeItem(EMAIL_MFA_CHALLENGE_KEY)
  }
}

const hydrateEmailMfaChallenge = (): PendingEmailMfaChallenge | null => {
  if (pendingEmailMfaChallenge.value) return pendingEmailMfaChallenge.value
  if (!import.meta.client) return null
  const raw = sessionStorage.getItem(EMAIL_MFA_CHALLENGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as PendingEmailMfaChallenge
    if (
      !parsed.challengeId
      || !parsed.browserVerifier
      || new Date(parsed.preparedExpiresAt).getTime() <= Date.now()
    ) {
      persistEmailMfaChallenge(null)
      return null
    }
    pendingEmailMfaChallenge.value = parsed
    return parsed
  }
  catch {
    persistEmailMfaChallenge(null)
    return null
  }
}

const createBrowserVerifier = (): string => {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

export const getPendingEmailMfaChallenge = (): PendingEmailMfaChallenge | null =>
  hydrateEmailMfaChallenge()

export const hasPendingEmailMfaChallenge = (): boolean =>
  Boolean(hydrateEmailMfaChallenge())

export const prepareEmailMfaChallenge = async (
  email: string,
  password: string,
): Promise<PendingEmailMfaChallenge | null> => {
  const browserVerifier = createBrowserVerifier()
  try {
    const api = createMfaApi({ auth: false })
    const result = await unwrapApiResponse(api.POST('/mfa/email/challenges/prepare', {
      body: {
        email: normalizeEmailInput(email),
        password,
        browser_verifier: browserVerifier,
      },
    }))
    if (
      !result.email_available
      || !result.challenge_id
      || !result.masked_email
      || !result.prepared_expires_at
    ) {
      persistEmailMfaChallenge(null)
      return null
    }
    const challenge: PendingEmailMfaChallenge = {
      challengeId: result.challenge_id,
      browserVerifier,
      maskedEmail: result.masked_email,
      preparedExpiresAt: new Date(result.prepared_expires_at).toISOString(),
      rememberDeviceDays: Number(result.remember_device_days ?? 0),
      codeExpiresAt: null,
      resendAvailableAt: null,
    }
    persistEmailMfaChallenge(challenge)
    return challenge
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Email MFA preparation failed')
  }
}

export const sendPendingEmailMfaCode = async (): Promise<PendingEmailMfaChallenge> => {
  const challenge = hydrateEmailMfaChallenge()
  if (!challenge) throw new Error('No pending email MFA challenge')
  try {
    const api = createMfaApi({ auth: false })
    const result = await unwrapApiResponse(api.POST('/mfa/email/challenges/{challenge_id}/send', {
      params: { path: { challenge_id: challenge.challengeId } },
      body: { browser_verifier: challenge.browserVerifier },
    }))
    const updated = {
      ...challenge,
      codeExpiresAt: new Date(result.code_expires_at).toISOString(),
      resendAvailableAt: new Date(result.resend_available_at).toISOString(),
    }
    persistEmailMfaChallenge(updated)
    return updated
  }
  catch (error) {
    if (error instanceof ApiResponseError) throw error
    return rethrowMfaApiError(error, 'Email MFA send failed')
  }
}

export const verifyPendingEmailMfaCode = async (code: string): Promise<string> => {
  const challenge = hydrateEmailMfaChallenge()
  if (!challenge) throw new Error('No pending email MFA challenge')
  try {
    const api = createMfaApi({ auth: false })
    const result = await unwrapApiResponse(api.POST('/mfa/email/challenges/{challenge_id}/verify', {
      params: { path: { challenge_id: challenge.challengeId } },
      body: {
        browser_verifier: challenge.browserVerifier,
        code: normalizeTotpCode(code),
      },
    }))
    persistEmailMfaChallenge(null)
    return result.custom_token
  }
  catch (error) {
    if (error instanceof ApiResponseError) throw error
    return rethrowMfaApiError(error, 'Email MFA verification failed')
  }
}

export const cancelPendingEmailMfaChallenge = async (): Promise<void> => {
  const challenge = hydrateEmailMfaChallenge()
  persistEmailMfaChallenge(null)
  if (!challenge) return
  try {
    const api = createMfaApi({ auth: false })
    await unwrapApiResponse(api.DELETE('/mfa/email/challenges/{challenge_id}', {
      params: { path: { challenge_id: challenge.challengeId } },
      body: { browser_verifier: challenge.browserVerifier },
    }))
  }
  catch (error) {
    $reportError('Email MFA challenge cancellation failed:', error)
  }
}
export const rememberTrustedDevice = async (rememberDevice: boolean): Promise<MfaStatus> => {
  try {
    const api = await createAuthenticatedMfaApi()
    return fromApiMfaStatus(await unwrapApiResponse(api.POST('/mfa/trusted-device/remember', {
      body: { remember_device: rememberDevice },
    })))
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Trusted device remember failed')
  }
}

export const restoreTrustedDeviceForEmail = async (
  email: string,
): Promise<TrustedDeviceRestoreResult> => {
  try {
    const api = createMfaApi({ auth: false, withCredentials: true })
    const normalizedEmail = normalizeEmailInput(email)
    return fromApiTrustedDeviceRestore(await unwrapApiResponse(api.POST(
      '/mfa/trusted-device/restore',
      { body: { email: normalizedEmail } },
    )))
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Trusted device restore failed')
  }
}

export const fetchRememberDevicePolicyForEmail = async (
  email: string,
): Promise<number> => {
  try {
    const api = createMfaApi({ auth: false, withCredentials: true })
    const normalizedEmail = normalizeEmailInput(email)
    const result = await unwrapApiResponse(api.POST(
      '/mfa/trusted-device/remember-policy',
      { body: { email: normalizedEmail } },
    ))
    return Number(result.remember_device_days ?? 0)
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Remember device policy request failed')
  }
}

export const revokeTrustedDevicesOnPasswordReset = async (oobCode: string): Promise<void> => {
  try {
    const api = createMfaApi({ auth: false, withCredentials: true })
    await unwrapApiResponse(api.POST('/mfa/trusted-devices/revoke-on-password-reset', {
      body: { oob_code: oobCode },
    }))
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Trusted device revoke on password reset failed')
  }
}

export const getTrustedDevices = async (): Promise<TrustedDevice[]> => {
  const token = await getCurrentToken()
  if (!token) return []

  try {
    const api = createMfaApi({ withCredentials: true })
    const result = await unwrapApiResponse(api.GET('/mfa/trusted-devices'))
    return Array.isArray(result.devices) ? result.devices.map(fromApiTrustedDevice) : []
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Trusted devices request failed')
  }
}

export const revokeTrustedDevice = async (trustedDeviceId: string): Promise<void> => {
  try {
    const api = await createAuthenticatedMfaApi()
    await unwrapApiResponse(api.DELETE('/mfa/trusted-devices/{trusted_device_id}', {
      params: { path: { trusted_device_id: trustedDeviceId } },
    }))
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Trusted device revoke failed')
  }
}

export const revokeAllTrustedDevices = async (): Promise<void> => {
  try {
    const api = await createAuthenticatedMfaApi()
    await unwrapApiResponse(api.DELETE('/mfa/trusted-devices'))
  }
  catch (error) {
    return rethrowMfaApiError(error, 'Trusted devices revoke failed')
  }
}

export const routeByMfaStatus = async (fallbackRoute: string = '/home'): Promise<boolean> => {
  try {
    const status = await syncMfaEnrollmentIfNeeded({ force: true })
    if (status?.enrollmentRequired) {
      await navigateTo({ name: NuxtRoutes.AuthMfaSetup })
      return true
    }
    if (status?.verificationRequired) {
      if (hasPendingEmailMfaChallenge()) {
        await navigateTo({ name: NuxtRoutes.AuthMfaChallenge })
        return true
      }
      return false
    }
    await navigateTo(fallbackRoute)
    return true
  }
  catch (error) {
    $reportError('Could not check MFA status during routing:', error)
    return false
  }
}

export const resolvePendingTotpSignIn = async (verificationCode: string) => {
  const resolver = pendingMfaResolver.value
  if (!resolver) {
    throw new Error('No pending MFA sign-in')
  }

  const totpHint = resolver.hints.find(
    hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  )

  if (!totpHint) {
    throw new Error('No MFA factor is available for this account')
  }

  const normalizedCode = normalizeTotpCode(verificationCode)
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    totpHint.uid,
    normalizedCode,
  )
  try {
    const credential = await resolver.resolveSignIn(assertion)
    clearPendingMfaResolver()
    return credential
  }
  catch (error) {
    if (isMfaChallengeSessionExpiredError(error)) {
      clearPendingMfaResolver()
    }
    throw error
  }
}

export const reauthenticateCurrentUserWithPassword = async (password: string) => {
  const user = getAuth().currentUser
  if (!user?.email) {
    throw new Error('No authenticated user')
  }

  const credential = EmailAuthProvider.credential(user.email, password)
  await reauthenticateWithCredential(user, credential)
}

export const startTotpReauthenticationWithPassword = async (
  password: string,
): Promise<TotpReauthenticationChallenge | null> => {
  try {
    await reauthenticateCurrentUserWithPassword(password)
    return null
  }
  catch (error) {
    if (!isMultiFactorAuthRequiredError(error)) {
      throw error
    }

    const resolver = getMultiFactorResolver(getAuth(), error as MultiFactorError)
    const totpHint = resolver.hints.find(
      hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    )

    if (!totpHint) {
      throw new Error('No TOTP factor is available for this account', { cause: error })
    }

    return {
      resolver,
      factorUid: totpHint.uid,
    }
  }
}

export const resolveTotpReauthentication = async (
  challenge: TotpReauthenticationChallenge,
  verificationCode: string,
) => {
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    challenge.factorUid,
    normalizeTotpCode(verificationCode),
  )
  await challenge.resolver.resolveSignIn(assertion)
}

export const beginTotpEnrollment = async (user: User) => {
  // Email MFA / trusted-device custom tokens are not a supported MFA first factor.
  // Fail early with the same code Firebase would return so the UI prompts for password.
  const tokenResult = await user.getIdTokenResult()
  if (tokenResult.signInProvider === 'custom') {
    throw Object.assign(
      new Error('UNSUPPORTED_FIRST_FACTOR : MFA is not available for the given first factor.'),
      { code: 'auth/unsupported-first-factor' },
    )
  }

  const session = await multiFactor(user).getSession()
  const secret = await TotpMultiFactorGenerator.generateSecret(session)
  const accountName = user.email ?? user.uid
  return {
    secret,
    qrCodeUrl: secret.generateQrCodeUrl(accountName, 'qutee.ai'),
    secretKey: secret.secretKey,
  }
}

export const completeTotpEnrollment = async (
  user: User,
  secret: TotpSecret,
  verificationCode: string,
) => {
  const normalizedCode = normalizeTotpCode(verificationCode)
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    secret,
    normalizedCode,
  )
  await multiFactor(user).enroll(assertion, 'Authenticator app')
  const refreshedUser = await reloadFirebaseUser(user)
  await refreshedUser.getIdToken(true)
  if (!isFirebaseTotpEnrolled(refreshedUser)) {
    throw Object.assign(new Error('TOTP enrollment did not complete in Firebase'), {
      code: 'auth/mfa-enrollment-incomplete',
    })
  }
  return await markTotpEnrolled()
}
